/**
 * Expense Breakdown Chart
 * 
 * Donut chart showing expense distribution:
 * - Fixed vs Variable expenses
 * - Category breakdown
 * - Interactive hover states
 * - Center summary
 * 
 * Used for visualizing expense continuations in Future Closure.
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

interface ExpenseCategory {
  category: string;
  amount: number;
  type: 'fixed' | 'variable';
  continuationRate?: number;
}

interface ExpenseBreakdownChartProps {
  data: ExpenseCategory[];
  title?: string;
  width?: number;
  height?: number;
  showContinuations?: boolean;
}

export const ExpenseBreakdownChart: React.FC<ExpenseBreakdownChartProps> = ({
  data,
  title = "Expense Breakdown",
  width = 720,
  height = 620,
  showContinuations = true
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 45, right: 25, bottom: 100, left: 25 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;
    const radius = Math.min(chartWidth, chartHeight) / 2 - 25;  // Larger overall circle
    const innerRadius = radius * 0.72;  // Even larger center hole
    const labelRadius = radius + 25;

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .style("font-family", "'Inter', -apple-system, sans-serif");

    // Title at top
    svg.append("text")
      .attr("x", width / 2)
      .attr("y", 25)
      .attr("text-anchor", "middle")
      .attr("fill", "#111827")
      .attr("font-size", "15px")
      .attr("font-weight", "600")
      .text(title);

    const g = svg.append("g")
      .attr("transform", `translate(${width / 2},${margin.top + chartHeight / 2})`);

    // Color scales
    const fixedColors = d3.scaleOrdinal<string>()
      .domain(data.filter(d => d.type === 'fixed').map(d => d.category))
      .range(['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#1D4ED8']);

    const variableColors = d3.scaleOrdinal<string>()
      .domain(data.filter(d => d.type === 'variable').map(d => d.category))
      .range(['#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#D97706']);

    const getColor = (d: ExpenseCategory) => {
      return d.type === 'fixed' ? fixedColors(d.category) : variableColors(d.category);
    };

    // Pie generator
    const pie = d3.pie<ExpenseCategory>()
      .value(d => Math.abs(d.amount))
      .sort(null);

    // Arc generator
    const arc = d3.arc<d3.PieArcDatum<ExpenseCategory>>()
      .innerRadius(innerRadius)
      .outerRadius(radius);

    const hoverArc = d3.arc<d3.PieArcDatum<ExpenseCategory>>()
      .innerRadius(innerRadius)
      .outerRadius(radius + 10);

    // Draw slices
    const slices = g.selectAll(".slice")
      .data(pie(data))
      .enter()
      .append("g")
      .attr("class", "slice");

    slices.append("path")
      .attr("d", arc)
      .attr("fill", d => getColor(d.data))
      .attr("stroke", "white")
      .attr("stroke-width", 2)
      .style("cursor", "pointer")
      .on("mouseenter", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", hoverArc);
        setHoveredCategory(d.data.category);
      })
      .on("mouseleave", function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arc);
        setHoveredCategory(null);
      });

    // Labels - positioned OUTSIDE the donut using labelRadius
    // Skip labels that would overlap with title or legend
    const labelArc = d3.arc<d3.PieArcDatum<ExpenseCategory>>()
      .innerRadius(labelRadius)
      .outerRadius(labelRadius);

    slices.append("text")
      .attr("transform", d => {
        const pos = labelArc.centroid(d);
        const midAngle = (d.startAngle + d.endAngle) / 2;
        
        // Adjust position for top labels to avoid title
        if (pos[1] < -radius * 0.5) {
          // Move label to the side instead of top
          const sign = pos[0] >= 0 ? 1 : -1;
          return `translate(${pos[0] + sign * 15},${pos[1] + 20})`;
        }
        
        // Adjust position for bottom labels to avoid legend
        if (pos[1] > radius * 0.6) {
          return `translate(${pos[0]},${pos[1] - 15})`;
        }
        
        return `translate(${pos[0]},${pos[1]})`;
      })
      .attr("text-anchor", d => {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        return midAngle < Math.PI ? "start" : "end";
      })
      .attr("fill", "#374151")
      .attr("font-size", "12px")
      .attr("font-weight", "600")
      .text(d => {
        const percentage = ((d.endAngle - d.startAngle) / (2 * Math.PI)) * 100;
        // Only show labels for slices > 6% to reduce clutter
        return percentage > 6 ? `${percentage.toFixed(0)}%` : '';
      });

    // Center text - use abbreviated format for large numbers
    const total = data.reduce((sum, d) => sum + Math.abs(d.amount), 0);
    const fixedTotal = data.filter(d => d.type === 'fixed').reduce((sum, d) => sum + Math.abs(d.amount), 0);
    
    // Format total with abbreviation for millions/thousands
    const formatTotal = (value: number) => {
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${d3.format(",.0f")(value)}`;
    };
    
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("y", -20)
      .attr("fill", "#6B7280")
      .attr("font-size", "13px")
      .attr("font-weight", "500")
      .text("Total Expenses");

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("y", 12)
      .attr("fill", "#111827")
      .attr("font-size", "26px")
      .attr("font-weight", "700")
      .text(formatTotal(total));

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("y", 38)
      .attr("fill", "#6B7280")
      .attr("font-size", "12px")
      .text(`${((fixedTotal / total) * 100).toFixed(0)}% Fixed`);

    // Legend with VALUES - for PDF export (shows actual amounts)
    const legendData = data.slice(0, 6); // Max 6 items in legend
    const legendY = height - 90;
    const legend = svg.append("g")
      .attr("transform", `translate(${width / 2 - 220}, ${legendY})`);

    const legendItems = legend.selectAll(".legend-item")
      .data(legendData)
      .enter()
      .append("g")
      .attr("class", "legend-item")
      .attr("transform", (d, i) => `translate(${(i % 3) * 155}, ${Math.floor(i / 3) * 20})`);

    legendItems.append("rect")
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", (d: ExpenseCategory) => getColor(d))
      .attr("rx", 2);

    // Category name + dollar value for PDF export
    legendItems.append("text")
      .attr("x", 15)
      .attr("y", 9)
      .attr("fill", "#4B5563")
      .attr("font-size", "10px")
      .text((d: ExpenseCategory) => {
        const name = d.category.length > 10 ? d.category.slice(0, 10) + '..' : d.category;
        const value = Math.abs(d.amount);
        const formatted = value >= 1000 ? `$${(value / 1000).toFixed(0)}K` : `$${value.toFixed(0)}`;
        return `${name}: ${formatted}`;
      });

    // Type legend (Fixed vs Variable) - centered below category legend
    const typeLegend = svg.append("g")
      .attr("transform", `translate(${width / 2 - 100}, ${height - 25})`);
    
    // Fixed
    typeLegend.append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", "#3B82F6")
      .attr("rx", 2);
    
    typeLegend.append("text")
      .attr("x", 15)
      .attr("y", 9)
      .attr("fill", "#4B5563")
      .attr("font-size", "10px")
      .text("Fixed");
    
    // Variable
    typeLegend.append("rect")
      .attr("x", 70)
      .attr("y", 0)
      .attr("width", 10)
      .attr("height", 10)
      .attr("fill", "#F59E0B")
      .attr("rx", 2);
    
    typeLegend.append("text")
      .attr("x", 85)
      .attr("y", 9)
      .attr("fill", "#4B5563")
      .attr("font-size", "10px")
      .text("Variable");

  }, [data, title, width, height]);

  // Tooltip
  const hoveredData = data.find(d => d.category === hoveredCategory);

  return (
    <div className="expense-breakdown-chart bg-white rounded-lg p-4 shadow-sm border relative">
      <svg ref={svgRef} />
      
      {/* Hover tooltip */}
      {hoveredData && (
        <div className="absolute top-16 right-4 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm shadow-lg">
          <div className="font-semibold">{hoveredData.category}</div>
          <div>${d3.format(",.0f")(Math.abs(hoveredData.amount))}</div>
          <div className="text-gray-300 text-xs mt-1">
            {hoveredData.type === 'fixed' ? '🔒 Fixed' : '📊 Variable'}
            {showContinuations && hoveredData.continuationRate !== undefined && (
              <span className="ml-2">
                {(hoveredData.continuationRate * 100).toFixed(0)}% continues
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseBreakdownChart;
